import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../components/ui/Badge';
import {
  Button,
  ChevronDownIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  SearchIcon,
  Select,
  ShipIcon,
  TicketIcon,
  TrashIcon,
  UserPlusIcon,
  RefreshIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getListReturnPath, openRecord } from '../../hooks/useNavigateBack';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { deleteCrewTravel } from '../../services/mutations';
import { CrewHelpHint } from './CrewReportSearchHeader';
import { crewChangesCostPrintPath } from './reports/crewChangesCostPrintMeta';
import { TRAVEL_TICKETS_HELP } from './travelTicketsHelp';
import type { CrewTravelKind, CrewTravelListResponse, CrewTravelRow, CrewTravelStatus } from './types';

const ROUTE = '/crew/crew-changes-cost';
const STORAGE_KEY = getListStateStorageKey(ROUTE);

type KindTab = 'all' | CrewTravelKind;

type Filters = {
  tab: KindTab;
  vesselId: string;
  status: string;
  search: string;
};

const kindTabs: Array<{
  id: KindTab;
  label: string;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'all',
    label: 'All',
    emptyTitle: 'No Crew Changes Cost records yet',
    emptyHint: 'Create a visit or changing-crew cost record to get started.',
  },
  {
    id: 'CREW_CHANGE',
    label: 'Changing crew',
    emptyTitle: 'No changing-crew cost records',
    emptyHint: 'Sign-off / sign-on travel packs will show up here.',
  },
  {
    id: 'VISIT',
    label: 'Visit',
    emptyTitle: 'No visit cost records',
    emptyHint: 'Superintendent, auditor, and guest travel will show up here.',
  },
];

function kindBadge(kind: CrewTravelKind): 'info' | 'default' {
  return kind === 'CREW_CHANGE' ? 'info' : 'default';
}

function statusBadge(status: CrewTravelStatus): 'default' | 'warning' | 'success' | 'danger' | 'info' {
  switch (status) {
    case 'DRAFT':
      return 'default';
    case 'BOOKED':
      return 'info';
    case 'ISSUED':
      return 'success';
    case 'CANCELLED':
      return 'danger';
    default:
      return 'default';
  }
}

function sortValue(row: CrewTravelRow, key: string): string | number {
  switch (key) {
    case 'kind':
      return row.kind;
    case 'status':
      return row.status;
    case 'vessel':
      return (row.vesselAbrv || row.vessel || '').toLowerCase();
    case 'passengers':
      return (row.passengerSummary || '').toLowerCase();
    case 'dateFrom':
      return row.dateFrom || '';
    case 'purpose':
      return (row.purpose || '').toLowerCase();
    case 'segments':
      return row.segmentCount;
    case 'totalCost':
      return row.totalCost ?? -1;
    default:
      return row.id;
  }
}

const NewTravelMenu: React.FC<{
  onSelect: (kind: CrewTravelKind) => void;
  className?: string;
}> = ({ onSelect, className }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items: Array<{
    kind: CrewTravelKind;
    title: string;
    hint: string;
    icon: React.ReactNode;
  }> = [
    {
      kind: 'VISIT',
      title: 'New Visit',
      hint: 'Superintendent, auditor, or guest travel',
      icon: <UserPlusIcon className="h-4 w-4" />,
    },
    {
      kind: 'CREW_CHANGE',
      title: 'New Crew Change',
      hint: 'Sign-off / sign-on travel pack',
      icon: <ShipIcon className="h-4 w-4" />,
    },
  ];

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <Button
        type="button"
        className="gap-1.5"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <PlusIcon className="h-4 w-4" />
        New
        <ChevronDownIcon className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </Button>
      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute right-0 z-40 mt-1.5 w-72 overflow-hidden rounded-xl border py-1 shadow-lg',
            colors.surface,
            colors.border,
          )}
        >
          {items.map((item) => (
            <button
              key={item.kind}
              type="button"
              role="menuitem"
              className={cn(
                'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors',
                colors.text,
                colors.interactiveHover,
              )}
              onClick={() => {
                setOpen(false);
                onSelect(item.kind);
              }}
            >
              <span
                className={cn(
                  'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                  isDark ? 'bg-white/10 text-sky-300' : 'bg-sky-50 text-sky-700',
                )}
              >
                {item.icon}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold leading-tight">{item.title}</span>
                <span className={cn('mt-0.5 block text-xs leading-snug', colors.textMuted)}>
                  {item.hint}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const TravelTicketsPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const listReturnPath = getListReturnPath(location);
  const confirm = useConfirm();
  const canEdit = can('crew', 'edit');
  const canDelete = can('crew', 'delete');

  const [persisted, setPersisted] = usePersistedListFilters<Filters>(STORAGE_KEY);
  const [tab, setTab] = useState<KindTab>(persisted?.tab ?? 'all');
  const [vesselId, setVesselId] = useState(persisted?.vesselId ?? '');
  const [status, setStatus] = useState(persisted?.status ?? '');
  const [searchInput, setSearchInput] = useState(persisted?.search ?? '');
  const [search, setSearch] = useState(persisted?.search ?? '');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'dateFrom', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'dateFrom' || key === 'segments' || key === 'totalCost' ? 'desc' : 'asc',
    },
  );

  useEffect(() => {
    setPersisted({ tab, vesselId, status, search });
  }, [tab, vesselId, status, search, setPersisted]);

  useEffect(() => {
    setPage(1);
  }, [tab, vesselId, status, search, itemsPerPage, sortConfig.key, sortConfig.direction]);

  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );

  const listUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (vesselId) params.set('vesselId', vesselId);
    if (tab !== 'all') params.set('kind', tab);
    if (status) params.set('status', status);
    if (search.trim()) params.set('q', search.trim());
    const qs = params.toString();
    return `/crew/travels${qs ? `?${qs}` : ''}`;
  }, [vesselId, tab, status, search]);

  const { data, error, isLoading, mutate } = useSWR<CrewTravelListResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const handleSearch = useCallback(() => {
    setSearch(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const sortedRows = useMemo(() => {
    const rows = [...(data?.rows ?? [])];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return (a.id - b.id) * dir;
    });
    return rows;
  }, [data?.rows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(search.trim() || vesselId || status || tab !== 'all');
  const activeTab = useMemo(() => kindTabs.find((t) => t.id === tab) ?? kindTabs[0], [tab]);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setTab('all');
    setVesselId('');
    setStatus('');
  };

  const openTicket = (id: number) => {
    openRecord(navigate, `${ROUTE}/${id}`, listReturnPath);
  };

  const handleDelete = useCallback(
    async (e: React.MouseEvent, row: CrewTravelRow) => {
      e.stopPropagation();
      const label = row.passengerSummary || row.purpose || `Ticket #${row.id}`;
      const ok = await confirm({
        title: 'Delete Crew Changes Cost',
        message: `Delete “${label}”?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmVariant: 'danger',
      });
      if (!ok) return;
      try {
        await deleteCrewTravel(row.id);
        await mutate();
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        window.alert(msg || 'Could not delete Crew Changes Cost record.');
      }
    },
    [confirm, mutate],
  );

  const createMenu = canEdit ? (
    <NewTravelMenu
      onSelect={(kind) => navigate(`${ROUTE}/new?kind=${encodeURIComponent(kind)}`)}
    />
  ) : null;

  return (
    <GeneralList
      headerStart={<CrewHelpHint hint={TRAVEL_TICKETS_HELP} className="self-center" />}
      tabs={kindTabs.map(({ id, label }) => ({ id, label }))}
      activeTab={tab}
      onTabChange={(value) => setTab(value as KindTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Traveler or purpose…',
        title: 'Searches traveler names and purpose',
        onSubmit: handleSearch,
      }}
      actions={
        <div className="flex h-10 items-center gap-2">
          <Button type="button" disabled={isLoading} onClick={handleSearch}>
            <SearchIcon className="h-4 w-4" />
            Search
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={() => void mutate()}
            title="Refresh"
          >
            <RefreshIcon className="h-4 w-4" />
            Refresh
          </Button>
          <Link
            to={crewChangesCostPrintPath({ tab, vesselId, status, search })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
          {hasFilters ? (
            <Button type="button" variant="outline" disabled={isLoading} onClick={clearFilters}>
              Clear
            </Button>
          ) : null}
          {createMenu}
        </div>
      }
      filtersInline
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[14rem] max-w-full">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder="Vessel"
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => setVesselId(String(e.target.value))}
            />
          </div>
          <div className="w-[9rem] max-w-full">
            <Select
              value={status}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'BOOKED', label: 'Booked' },
                { value: 'ISSUED', label: 'Issued' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
              placeholder="Status"
              showClearButton
              onChange={(e) => setStatus(String(e.target.value))}
            />
          </div>
        </div>
      }
      hasFilters={hasFilters}
      isLoading={isLoading}
      loadingLabel="Loading Crew Changes Cost…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon: <TicketIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching records' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          createMenu ?? undefined
        ),
      }}
      banner={
        error ? (
          <p className={cn('rounded-xl border px-3 py-2 text-sm', colors.error)}>
            Could not load Crew Changes Cost.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-[7.5rem] font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="kind" className="whitespace-nowrap">
              Type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="whitespace-nowrap">
              Status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel">Vessel</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="passengers">Travelers</GeneralListSortableTh>
            <GeneralListSortableTh sortKey="dateFrom" className="whitespace-nowrap">
              Dates
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="purpose" className="hidden lg:table-cell">
              Purpose
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="totalCost" className="whitespace-nowrap" align="right">
              Cost
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="segments" className="hidden md:table-cell" align="right">
              Legs
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const dates =
              [row.dateFrom ? formatDate(row.dateFrom) : null, row.dateTo ? formatDate(row.dateTo) : null]
                .filter(Boolean)
                .join(' → ') || '—';
            return (
              <tr
                key={row.id}
                onClick={() => openTicket(row.id)}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-nowrap items-center gap-0.5">
                    <Link
                      to={`${ROUTE}/${row.id}`}
                      state={{ from: listReturnPath }}
                      title="View"
                      aria-label="View Crew Changes Cost"
                      className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`${ROUTE}/${row.id}/edit`}
                      state={{ from: listReturnPath }}
                      title="Edit"
                      aria-label="Edit Crew Changes Cost"
                      className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    {canDelete ? (
                      <button
                        type="button"
                        title="Delete"
                        aria-label="Delete Crew Changes Cost"
                        onClick={(e) => void handleDelete(e, row)}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          isDark ? 'text-red-400' : 'text-red-600',
                          colors.interactiveHover,
                        )}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </td>
                <td>
                  <Badge variant={kindBadge(row.kind)} className="rounded-md px-1.5 py-0 text-[10px]">
                    {row.kind === 'CREW_CHANGE' ? 'Changing crew' : 'Visit'}
                  </Badge>
                </td>
                <td>
                  <Badge variant={statusBadge(row.status)} className="rounded-md px-1.5 py-0 text-[10px]">
                    {row.status}
                  </Badge>
                </td>
                <td className={cn('font-medium', colors.text)}>{row.vesselAbrv || row.vessel || '—'}</td>
                <td className={colors.text}>{row.passengerSummary || '—'}</td>
                <td className={cn('whitespace-nowrap', colors.textMuted)}>{dates}</td>
                <td className={cn('hidden lg:table-cell', colors.textMuted)}>{row.purpose || '—'}</td>
                <td className={cn('whitespace-nowrap text-right font-medium tabular-nums', colors.text)}>
                  {row.costSummary || '—'}
                </td>
                <td className={cn('hidden text-right md:table-cell', colors.textMuted)}>{row.segmentCount}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default TravelTicketsPage;
